import React, { useMemo, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Hash, ChevronDown, X as XIcon, Search } from 'lucide-react';

/**
 * ReferenceFilter — multi-select popover for filtering by Reference No.
 *
 * Used across the app (Orders, Dashboard Pending, Goldsmith Detail,
 * Production Timeline). Mirrors the existing "Pending" multi-select popover
 * pattern so it feels native. Options are the distinct reference numbers
 * available in the current data set; the caller owns `value` (array of
 * numbers) and `onChange`.
 *
 * Props:
 *   options       number[]  distinct reference numbers to choose from
 *   value         number[]  currently selected reference numbers
 *   onChange      (next: number[]) => void
 *   counts        Map<number, number>  optional per-ref match counts
 *   testIdPrefix  string    data-testid namespace (default 'reference-filter')
 *   label         string    trigger label (default 'Reference No.')
 */
export function ReferenceFilter({
  options = [],
  value = [],
  onChange,
  counts,
  testIdPrefix = 'reference-filter',
  label = 'Page No.',
}) {
  const [q, setQ] = useState('');
  // Short single-word form for the compact trigger / chip labels.
  const labelShort = (label.split(' ')[0] || label).replace(/\.$/, '');
  const selectedSet = useMemo(() => new Set(value || []), [value]);

  const sortedOptions = useMemo(() => {
    const uniq = Array.from(new Set((options || []).filter((r) => r != null)));
    // Newest / highest reference first — mirrors how operators think.
    uniq.sort((a, b) => Number(b) - Number(a));
    return uniq;
  }, [options]);

  const filtered = useMemo(() => {
    const term = q.trim();
    if (!term) return sortedOptions;
    return sortedOptions.filter((r) => String(r).includes(term));
  }, [sortedOptions, q]);

  const toggle = (ref) => {
    const cur = new Set(value || []);
    if (cur.has(ref)) cur.delete(ref); else cur.add(ref);
    onChange(Array.from(cur));
  };

  const active = (value || []).length > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 h-9 rounded-md border px-3 text-[12.5px] transition-colors ${
            active
              ? 'border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100'
              : 'border-[var(--divider)] bg-white text-slate-700 hover:bg-slate-50'
          }`}
          data-testid={`${testIdPrefix}-trigger`}
          title={`Filter by ${label}`}
        >
          <Hash size={13} />
          <span>
            {active
              ? `${labelShort} · ${(value || []).length} selected`
              : label}
          </span>
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-3"
        align="start"
        data-testid={`${testIdPrefix}-content`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
            {label}
          </div>
          {active && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[11px] text-slate-500 hover:text-slate-800 underline"
              data-testid={`${testIdPrefix}-clear`}
            >
              Clear
            </button>
          )}
        </div>
        {sortedOptions.length > 8 && (
          <div className="relative mb-2">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="h-8 pl-7 text-[12.5px]"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
        )}
        <div className="max-h-60 overflow-y-auto grid grid-cols-1 gap-1">
          {filtered.length === 0 && (
            <div className="text-[11px] text-slate-500 py-2 text-center">
              {`No ${labelShort.toLowerCase()}s.`}
            </div>
          )}
          {filtered.map((ref) => {
            const isOn = selectedSet.has(ref);
            const count = counts ? (counts.get(ref) || 0) : null;
            return (
              <label
                key={ref}
                className={`flex items-center justify-between gap-2 rounded-md px-2 py-1.5 cursor-pointer border ${
                  isOn ? 'bg-indigo-50 border-indigo-200' : 'border-transparent hover:bg-slate-50'
                }`}
                data-testid={`${testIdPrefix}-option-${ref}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Checkbox
                    checked={isOn}
                    onCheckedChange={() => toggle(ref)}
                    data-testid={`${testIdPrefix}-option-${ref}-checkbox`}
                  />
                  <span className="text-sm font-mono tabular-nums text-slate-800">{ref}</span>
                </div>
                {count != null && (
                  <span className="text-[11px] font-mono tabular-nums text-slate-500">{count}</span>
                )}
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * ReferenceFilterChips — removable chips for the currently selected
 * reference numbers. Renders nothing when no reference is selected.
 */
export function ReferenceFilterChips({
  value = [],
  onChange,
  testIdPrefix = 'reference-filter-chip',
  label = 'Page No.',
}) {
  if (!Array.isArray(value) || value.length === 0) return null;
  const labelShort = (label.split(' ')[0] || label).replace(/\.$/, '');
  const remove = (ref) => onChange((value || []).filter((r) => r !== ref));
  return (
    <>
      {value.map((ref) => (
        <span
          key={ref}
          className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium bg-indigo-50 text-indigo-700 border-indigo-200"
          data-testid={`${testIdPrefix}-${ref}`}
        >
          <span className="text-[10px] uppercase tracking-wide opacity-70">{labelShort}</span>
          <span className="font-mono tabular-nums">{ref}</span>
          <button
            type="button"
            onClick={() => remove(ref)}
            className="ml-0.5 inline-flex items-center rounded-full hover:bg-black/10 p-0.5 transition-colors"
            aria-label={`Remove ${labelShort.toLowerCase()} ${ref} filter`}
            data-testid={`${testIdPrefix}-${ref}-remove`}
          >
            <XIcon size={11} />
          </button>
        </span>
      ))}
    </>
  );
}
